import { useEffect, useState } from 'react'
import Icon from './Icons'
import { api, storeSession, storedSession } from './api'
import BillingView from './BillingView'
import DeviceDetail from './DeviceDetail'
import DeviceList from './DeviceList'
import Login from './Login'
import TeamView from './TeamView'

type Theme = 'light' | 'dark'
type View = 'devices' | 'billing' | 'team'

export default function App(): React.JSX.Element {
  const [signedIn, setSignedIn] = useState(() => storedSession() !== null)
  const [view, setView] = useState<View>('devices')
  const [openDevice, setOpenDevice] = useState<number | null>(null)
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem('yts.theme') as Theme) ?? 'light'
  )

  useEffect(() => {
    document.documentElement.dataset.theme = theme
    localStorage.setItem('yts.theme', theme)
  }, [theme])

  // Any call can discover the session expired; api.ts clears the stored token in
  // that case, so re-checking on focus drops us back to the login screen instead
  // of leaving a console that silently fails every request.
  useEffect(() => {
    const check = (): void => setSignedIn(storedSession() !== null)
    window.addEventListener('focus', check)
    const timer = setInterval(check, 5000)
    return () => {
      window.removeEventListener('focus', check)
      clearInterval(timer)
    }
  }, [])

  const signOut = async (): Promise<void> => {
    const token = storedSession()
    storeSession(null)
    setSignedIn(false)
    setOpenDevice(null)
    // Best effort: the local token is already gone, so a failure here only leaves
    // a row to expire on its own.
    if (token) await api.logout(token).catch(() => undefined)
  }

  const toggleTheme = (): void => setTheme(theme === 'light' ? 'dark' : 'light')

  if (!signedIn) {
    return (
      <>
        {/* Pinned to the corner — on the signed-out page there is no topbar to
            hold it, and unpositioned it lands above the centred card. */}
        <div className="theme-float">
          <ThemeButton theme={theme} onToggle={toggleTheme} />
        </div>
        <Login onSignedIn={() => setSignedIn(true)} />
      </>
    )
  }

  return (
    <div className="app">
      <header className="topbar">
        <div className="brand">
          <BrandMark />
          <span>
            Smart Live Streamer
            <small>fleet console</small>
          </span>
        </div>
        <div className="spacer" />
        <ThemeButton theme={theme} onToggle={toggleTheme} />
        <button onClick={() => void signOut()}>
          <Icon name="logout" />
          Sign out
        </button>
      </header>

      {/* Tabs sit on their own row rather than beside the title: the row is the
          full width of the console, so the active underline lines up with the
          content it switches, and neither one crowds the other on a phone. */}
      <nav className="tabs">
        <button
          className={view === 'devices' ? 'on' : ''}
          onClick={() => {
            setView('devices')
            setOpenDevice(null)
          }}
        >
          <Icon name="monitor" />
          Devices
        </button>
        {/* Shown to everyone: a customer's own operator sees only their own
            account here, which is the point — it answers "when does our licence
            end" without a phone call. */}
        <button className={view === 'billing' ? 'on' : ''} onClick={() => setView('billing')}>
          <Icon name="clock" />
          Licensing
        </button>
        <button className={view === 'team' ? 'on' : ''} onClick={() => setView('team')}>
          <Icon name="users" />
          Team
        </button>
      </nav>

      <main>
        {view === 'billing' ? (
          <BillingView />
        ) : view === 'team' ? (
          <TeamView />
        ) : openDevice === null ? (
          <DeviceList onOpen={setOpenDevice} />
        ) : (
          <DeviceDetail deviceId={openDevice} onBack={() => setOpenDevice(null)} />
        )}
      </main>
    </div>
  )
}

/** The client's tray/window mark, so the console and the desktop app share a face. */
function BrandMark(): React.JSX.Element {
  return (
    <span className="mark">
      <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <path d="M1 4.2A2.2 2.2 0 0 1 3.2 2h5.6A2.2 2.2 0 0 1 11 4.2v7.6A2.2 2.2 0 0 1 8.8 14H3.2A2.2 2.2 0 0 1 1 11.8V4.2Zm11 2.4 2.6-1.8a.6.6 0 0 1 .9.5v5.4a.6.6 0 0 1-.9.5L12 9.4V6.6Z" />
      </svg>
    </span>
  )
}

function ThemeButton({
  theme,
  onToggle
}: {
  theme: Theme
  onToggle: () => void
}): React.JSX.Element {
  return (
    <button className="icon" onClick={onToggle} title="Toggle theme">
      <Icon name={theme === 'light' ? 'moon' : 'sun'} />
    </button>
  )
}
